import { useState } from "react";
import { API } from "../lib/api";
import { fmt } from "../lib/calc";
import { X, Ruler, Check } from "lucide-react";

// User confirms real panel dimensions after import. Length is primary;
// width auto-derives from the exact title-block area (width = area / length).
export function ImportReview({ panels, onClose, onConfirm }) {
  const [rows, setRows] = useState(() =>
    panels.map((p) => ({ id: p.id, name: p.name, jobNumber: p.jobNumber, area: p.area, drawingId: p.drawingId, color: p.color, width: p.width, length: p.length }))
  );

  const setLength = (id, val) =>
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        const length = parseFloat(val);
        const width = length > 0 && r.area > 0 ? +(r.area / length).toFixed(2) : r.width;
        return { ...r, length: val, width };
      })
    );

  const setWidth = (id, val) =>
    setRows((prev) =>
      prev.map((r) => {
        if (r.id !== id) return r;
        const width = parseFloat(val);
        const length = width > 0 && r.area > 0 ? +(r.area / width).toFixed(2) : r.length;
        return { ...r, width: val, length };
      })
    );

  const confirm = () => {
    onConfirm(
      rows.map((r) => ({
        id: r.id,
        width: Math.max(0, parseFloat(r.width) || 0),
        length: Math.max(0, parseFloat(r.length) || 0),
      }))
    );
  };

  return (
    <div
      data-testid="import-review-modal"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
    >
      <div className="w-full max-w-2xl max-h-[92vh] flex flex-col bg-[#16181A] border border-[#2E3033]">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#2E3033] shrink-0">
          <div className="flex items-center gap-2">
            <Ruler className="w-4 h-4 text-[#2560C9]" />
            <h3 className="font-heading font-bold text-white text-sm tracking-tight">CONFIRM PANEL DIMENSIONS</h3>
          </div>
          <button data-testid="import-review-close" onClick={onClose} className="text-[#6E7787] hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-4 py-2.5 border-b border-[#2E3033] shrink-0">
          <p className="text-[11px] font-mono text-[#A0AABF]">
            These footprints were estimated from the drawing outline. Enter the panel <b className="text-white">length</b> from
            the outside dimensions on the drawing — width is calculated from the panel area automatically. Adjust either field.
          </p>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {rows.map((r) => (
            <div key={r.id} data-testid={`review-row-${r.id}`} className="flex items-center gap-3 bg-[#0A0A0A] border border-[#2E3033] p-2.5">
              {r.drawingId ? (
                <a href={`${API}/panels/drawing/${r.drawingId}`} target="_blank" rel="noreferrer" className="shrink-0">
                  <img
                    src={`${API}/panels/drawing/${r.drawingId}`}
                    alt={r.name}
                    className="w-24 h-20 object-cover bg-white border border-[#2E3033]"
                  />
                </a>
              ) : (
                <div className="w-24 h-20 shrink-0 bg-[#16181A] border border-dashed border-[#2E3033] flex items-center justify-center text-[9px] font-mono text-[#6E7787]">
                  no drawing
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <span className="w-2 h-2 shrink-0" style={{ backgroundColor: r.color }} />
                  <span className="font-heading font-bold text-white text-sm truncate">{r.name}</span>
                  {r.jobNumber && (
                    <span className="text-[8px] font-mono uppercase tracking-wider px-1 py-0.5 border border-[#2560C9]/50 text-[#3B75DF]">
                      {r.jobNumber}
                    </span>
                  )}
                </div>
                <p className="text-[10px] font-mono text-[#6E7787] mt-1">Area {fmt(r.area)} m²</p>
                <div className="mt-2 flex items-center gap-3">
                  <label className="flex items-center gap-1.5">
                    <span className="text-[9px] font-mono uppercase tracking-wider text-[#6E7787]">Length (m)</span>
                    <input
                      data-testid={`review-length-${r.id}`}
                      type="number"
                      min="0"
                      step="0.05"
                      value={r.length}
                      onChange={(e) => setLength(r.id, e.target.value)}
                      className="w-20 bg-[#16181A] border border-[#2E3033] text-white text-xs font-mono px-2 py-1 focus:border-[#2560C9] focus:outline-none"
                    />
                  </label>
                  <label className="flex items-center gap-1.5">
                    <span className="text-[9px] font-mono uppercase tracking-wider text-[#6E7787]">Width (m)</span>
                    <input
                      data-testid={`review-width-${r.id}`}
                      type="number"
                      min="0"
                      step="0.05"
                      value={r.width}
                      onChange={(e) => setWidth(r.id, e.target.value)}
                      className="w-20 bg-[#16181A] border border-[#2E3033] text-white text-xs font-mono px-2 py-1 focus:border-[#2560C9] focus:outline-none"
                    />
                  </label>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-[#2E3033] shrink-0">
          <button
            data-testid="import-review-skip"
            onClick={onClose}
            className="border border-[#2E3033] hover:bg-[#22252A] text-[#A0AABF] text-xs px-3 py-1.5 transition-colors"
          >
            Skip for now
          </button>
          <button
            data-testid="import-review-apply"
            onClick={confirm}
            className="flex items-center gap-1.5 bg-[#2560C9] hover:bg-[#3B75DF] text-white text-xs px-4 py-1.5 transition-colors"
          >
            <Check className="w-3.5 h-3.5" /> Apply Dimensions
          </button>
        </div>
      </div>
    </div>
  );
}
